import { plainToClass } from 'class-transformer';
import { inject, injectable } from 'inversify';
import { ReplaySubject, race, timer } from 'rxjs';
import { delay, filter, first, mergeMap, retryWhen, take } from 'rxjs/operators';

import { BrowserUtils } from '../ui-utils/browser-utils';
import { PSDate } from '../utils/date.utils';
import { MotiveConfig, MotiveSettingsResponse, MotiveSlotConfig } from '../utils/request.utils';
import { ApiConfig } from './api-config';
import { LightHttp } from './light-http.service';
import { LoginService } from './login.service';
import { SettingsService } from './settings.service';
import { UIContext } from './ui-context';
import { MotiveDisabledDate, UserPreferenceService, UserPreferenceTypes } from './user-preference.service';
import { WindowService } from './window.service';
import { ScriptLoadService } from './script-load.service';


export declare namespace GoogleMotive {
    export const DOWNLOAD_PAGE_TOP = '4411354276';
    export const DOWNLOAD_PAGE_LEFT = '1772269566';
    export const DOWNLOAD_PAGE_RIGHT = '4206861217';
    export const DOWNLOAD_PAGE_BOTTOM = '5895347409';
}

/** How long the AdSense library gets to announce itself before the page treats it as blocked. */
export const ADSENSE_LOAD_DEADLINE_MS = 12_000;
/** How long the watch keeps looking for a late library (un-blocks if it arrives). */
export const ADSENSE_LOAD_GIVE_UP_MS = 45_000;
export const ADSENSE_LOAD_POLL_MS = 500;

@injectable()
export class MotivesService {
    private _initialMotives: MotiveSettingsResponse;
    get initialMotives(): MotiveSettingsResponse {
        return this._initialMotives;
    }
    set initialMotives(_initialMotives: MotiveSettingsResponse) {
        this._initialMotives = _initialMotives;
        this.setMotives(this._initialMotives);
        this.updateCurrentUserMotiveConfig();
    }

    get freeUserMotives(): MotiveConfig {
        return this.initialMotives ? this.initialMotives.free : {} as any;
    }

    private motivesEnabled = true;
    private forceNoMotives = false;
    canDisplayAdsense = false;
    motiveSourceLoadStarted = false;
    /**
     * Round 13 (Z4): the AdSense library never announced itself (`window.adsbygoogle.loaded`) within
     * `ADSENSE_LOAD_DEADLINE_MS` of being asked for -- an ad blocker, a tracking-protection list or a
     * network that drops `pagead2.googlesyndication.com`. While true every gate in the effective
     * document reads false, so the site's slots and the editors' units all render nothing instead of
     * a blank box the size of an ad. Clears itself if the library does arrive late.
     *
     * Why not the load promise: `ScriptLoadService.loadAdsense` resolves as soon as `window.adsbygoogle`
     * exists, and the first component's `push` creates that array itself ~400 ms after mount -- so a
     * blocked script has always looked "loaded" from here.
     */
    adsenseBlocked = false;
    private adsenseWatch: ReturnType<typeof setInterval> | null = null;


    get internalAdsEnabled() {
        // TODO need to check if a user is a paid user
        return this.motives && this.motives && this.motives.free.internalMotivesEnabled;
    }

    get motiveReady(): boolean {
        return !this.forceNoMotives && this.canDisplayAdsense;
    }

    readonly _onMotiveSettingsChange = new ReplaySubject<MotiveSettingsResponse>(1);
    get onMotiveSettingsChange() {
        return this._onMotiveSettingsChange.asObservable();
    }

    readonly _onEffectiveMotiveSettingsChange = new ReplaySubject<MotiveConfig>(1);
    get onEffectiveMotiveSettingsChange() {
        return this._onEffectiveMotiveSettingsChange.asObservable();
    }

    private _effectiveMotives: MotiveConfig;
    get effectiveMotives(): MotiveConfig {
        return this._effectiveMotives;
    }

    private _motiveConfig: MotiveConfig;
    get currentUserMotiveConfig() {
        return this._motiveConfig;
    }

    private motives: MotiveSettingsResponse;

    constructor(
        @inject(SettingsService) private settingsService: SettingsService,
        @inject(UserPreferenceService) private userPreferenceService: UserPreferenceService,
        @inject(LoginService) private loginService: LoginService,
        @inject(UIContext) private uiContext: UIContext,
        @inject(LightHttp) private http: LightHttp,
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(WindowService) private windowService: WindowService,
        @inject(ScriptLoadService) private scriptLoadService: ScriptLoadService,
    ) {
        const disabledDate = this.userPreferenceService.getPreference<MotiveDisabledDate>(UserPreferenceTypes.MOTIVES_DISABLED_DATE, MotiveDisabledDate);
        if (disabledDate && disabledDate.date) {
            if (PSDate.from(disabledDate.date).isAfter(PSDate.from(new Date).subtract(1, 'hours'))) {
                this.motivesEnabled = false;
            }
        }
        this.loginService.onUserDetailsChanged.subscribe(
            () => {
                this.cachePaidUserStatus();
                this.updateCurrentUserMotiveConfig();
                this.calculateEffictiveMotives();
            },
        );

        this.settingsService.userSettings.subscribe({
            next: (userSettings) => {
                this.setMotives(userSettings.motive);
                this.updateCurrentUserMotiveConfig();
                this.calculateEffictiveMotives();
            },
        });

        this.userPreferenceService.preferenceChange.pipe(
            filter(val => val.preferenceKey === UserPreferenceTypes.MOTIVES_DISABLED_DATE)
        ).subscribe({
            next: (preference) => {
                const disabledDate: MotiveDisabledDate = preference.value;
                if (disabledDate && disabledDate.date && PSDate.from(disabledDate.date).isAfter(PSDate.from(new Date).subtract(1, 'hours'))) {
                    this.motivesEnabled = false;
                } else {
                    this.motivesEnabled = true;
                }
                this.calculateEffictiveMotives();
            },
        });
    }

    initialize() {
        this.loadMotiveSource();
        try {
            if (!BrowserUtils.hasDocument()) {
                console.error("Requested execution aborted since document was not avalilable");
                return;
            }
            this.forceNoMotives = !!document.location.search.match(/noMotives/);
        } catch {
            // skip
        }
    }

    private async loadMotiveSource() {
        try {
            if (!this.motiveSourceLoadStarted) {
                this.motiveSourceLoadStarted = true;
                const cached = this.userPreferenceService.getPreference<boolean>(UserPreferenceTypes.CACHED_IS_PAID_USER);
                if (cached === true) {
                    this.canDisplayAdsense = false;
                    return;
                }
                if (cached === false) {
                    await this.loadAdsenseScript();
                    return;
                }
                // Wait for login response, but timeout after 10s to avoid hanging forever
                await new Promise<void>((resolve) => {
                    race(
                        this.loginService.onUserDetailsChanged.pipe(first()),
                        timer(10000)
                    ).subscribe(() => resolve());
                });
                if (this.loginService.userDetails && this.loginService.userDetails.isPaidUser()) {
                    this.canDisplayAdsense = false;
                    return;
                }
                await this.loadAdsenseScript();
            }
        } catch (e) {
            // if (isPlatformBrowser(this.platformId)) {
            this.uiContext.errorToast(
                `Please consider whitelisting xconvert.com on your AdBlocker.`,
                'Hi Friend',
                false,
                false
            );
            try {
                this.saveMotiveStatics(false);
            } catch (e) {
                // Too much to handle. calling log here will case recursve call to ths fn
            }
            // }
        }
    }

    public saveMotiveStatics(enabled: boolean) {
        try {
            const userEndpoint = '/users';
            const sessionDataEndPoint = `${userEndpoint}/session/info`;
            this.windowService.userClicked.pipe(
                mergeMap(() => {
                    return this.http.post(
                        `${this.apiConfig.getHttpsEndpoint()}${sessionDataEndPoint}`,
                        { enabled },
                        {
                            headers: { 'Content-Type': 'application/json' },
                        });
                }),
                retryWhen(errors => errors.pipe(delay(1000), take(2))),
            ).subscribe({
                error: (err) => {
                    // skip
                }
            });
        } catch (e) {
            // not need
        }
    }

    private calculateEffictiveMotives() {
        const currentUserMotiveConfig = plainToClass(MotiveSlotConfig, this.currentUserMotiveConfig || {}, { strategy: 'excludeAll' });
        const _effectiveMotives = Object.assign({}, this.currentUserMotiveConfig, currentUserMotiveConfig);
        const adsOn = this.motivesEnabled && !this.adsenseBlocked;
        // CLS lane (review 7, owner ruling 2026-09-27: "we must NOT collapse ad slots"): the gates below say whether an
        // AD is requested; `reservedSlots` says whether the SPACE stays. It is the served document's own gate for this
        // visitor (free / paid), unmasked by the two runtime facts -- the ad library found blocked (~1.4 s in, 0.083 CLS on
        // the phone converter as the below-tool slot went) and an ad closed within the hour. Those keep a blank box;
        // only the served document (a paid account, a position switched off) takes the box away. See `adSpaceFor`.
        const reservedSlots: { [key: string]: boolean } = {};
        Object.keys(currentUserMotiveConfig).forEach(key => {
            _effectiveMotives[key] = this.currentUserMotiveConfig[key] && adsOn;
            reservedSlots[key] = !!this.currentUserMotiveConfig[key];
        });
        (_effectiveMotives as MotiveConfig & { reservedSlots?: { [key: string]: boolean } }).reservedSlots = reservedSlots;

        this._effectiveMotives = _effectiveMotives;
        this._onEffectiveMotiveSettingsChange.next(this.effectiveMotives);
    }

    private async loadAdsenseScript() {
        // R14-Q16: the watch runs from the moment the library is ASKED for, not from when the load promise
        // settles -- a load that hangs or rejects still gets the deadline verdict (measured before: the
        // watch started ~1.4 s in and the boxes collapsed at 13-15 s; on a first visit the 10 s login race
        // in `loadMotiveSource` came first, so `data-gate` still read true at +20 s).
        this.watchAdsenseLoaded();
        try {
            await this.scriptLoadService.loadAdsense();
        } catch (e) {
            // The script itself failed (`ScriptLoadService.loadAdsense` rejects on the element's `error`):
            // blocked NOW, one publication, and the watch keeps looking so a late library un-blocks.
            this.markAdsenseBlocked();
            try {
                this.saveMotiveStatics(false);
            } catch (e2) {
                // Too much to handle. calling log here will cause recursive call to this fn
            }
            return;
        }
        const w = window as any;
        w.adsbygoogle = w.adsbygoogle || [];
        this.canDisplayAdsense = true;
        try {
            this.saveMotiveStatics(true);
        } catch (e) {
            // Too much to handle. calling log here will cause recursive call to this fn
        }
    }

    /**
     * Poll for the library's own `adsbygoogle.loaded = true`. Past the deadline without it: blocked
     * (gates off, subscribers re-evaluate). Keep looking a while longer so a slow network un-blocks.
     * Injectable clock for tests.
     */
    watchAdsenseLoaded(now: () => number = Date.now, every = ADSENSE_LOAD_POLL_MS): void {
        if (this.adsenseWatch !== null || !BrowserUtils.hasWindow()) return;
        const started = now();
        const loaded = () => (window as any).adsbygoogle?.loaded === true;
        const stop = () => { if (this.adsenseWatch !== null) { clearInterval(this.adsenseWatch); this.adsenseWatch = null; } };
        this.adsenseWatch = setInterval(() => {
            const waited = now() - started;
            if (loaded()) {
                stop();
                if (this.adsenseBlocked) { this.adsenseBlocked = false; this.calculateEffictiveMotives(); }
                return;
            }
            // Deadline first, give-up second: a throttled background tab can wake past both marks in
            // one tick and must still record the verdict.
            if (waited >= ADSENSE_LOAD_DEADLINE_MS) this.markAdsenseBlocked();
            if (waited >= ADSENSE_LOAD_GIVE_UP_MS) { stop(); return; }
        }, every);
    }

    /**
     * The one place the verdict is recorded: `adsenseBlocked = true` and ONE publication of the effective
     * document (every gate false) -- idempotent, so the script's `error` (R14-Q16) and the watch's deadline
     * can both arrive and the subscribers hear it once. The watch alone clears it (a late library).
     */
    private markAdsenseBlocked(): void {
        if (this.adsenseBlocked) return;
        this.adsenseBlocked = true;
        this.calculateEffictiveMotives();
    }

    private cachePaidUserStatus() {
        const isPaid = this.loginService.userDetails ? this.loginService.userDetails.isPaidUser() : false;
        this.userPreferenceService.setPreference(UserPreferenceTypes.CACHED_IS_PAID_USER, isPaid);
    }

    private updateCurrentUserMotiveConfig() {
        if (this.motives) {
            if (this.loginService.userDetails && this.loginService.userDetails.isPaidUser()) {
                this._motiveConfig = this.motives.paid;
            } else {
                this._motiveConfig = this.motives.free;
            }
        } else {
            this._motiveConfig = {} as any;
        }
    }

    private setMotives(motives: MotiveSettingsResponse) {
        this.motives = motives;
        this._onMotiveSettingsChange.next(motives);
    }
}
